import React, { useState, useCallback, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import plusIcon from "../../../assets/shapes_svg/ambition/plus-icon.svg";
import { useUserAccess } from "../../../components/WriteAccessContext";

const DraggableLineItem = ({ id, index, moveItem, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "LINE_ITEM",
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "LINE_ITEM",
    hover(item) {
      if (item.index !== index) {
        moveItem(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className="line-item"
    >
      {children}
    </div>
  );
};

const LineItemsForm = ({
  onSubmit,
  initialData = [],
  showAmountFields = true,
  mode = "investment",
}) => {
  const { userAccess } = useUserAccess();
  const [lineItems, setLineItems] = useState(
    initialData.length
      ? initialData
      : [
          {
            id: "1",
            title: "",
            type: "",
            ...(showAmountFields && { amount: "", unit: "USD" }),
          },
        ]
  );

  const [dirtyItems, setDirtyItems] = useState(new Set());
  const units = ["DKK", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY"];

  useEffect(() => {
    if (initialData.length > 0) {
      setLineItems(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    // Auto-resize all textareas on mount and when data changes
    const textareas = document.querySelectorAll("textarea.input");
    textareas.forEach((textarea) => {
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    });
  }, [lineItems]);

  const moveItem = useCallback(
    (dragIndex, hoverIndex) => {
      setLineItems((prevItems) => {
        const newItems = [...prevItems];
        const dragItem = newItems[dragIndex];
        newItems.splice(dragIndex, 1);
        newItems.splice(hoverIndex, 0, dragItem);
        return newItems;
      });
      // Mark all items as dirty when reordering
      setDirtyItems(new Set(lineItems.map((item) => item.id)));
    },
    [lineItems]
  );

  const handleAddLine = () => {
    const newItem = {
      id: String(Date.now()), // Use timestamp for unique IDs
      title: "",
      type: "",
      ...(showAmountFields && { amount: "", unit: "USD" }),
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const handleRemoveLine = (id) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((item) => item.id !== id));
    // Submit the deletion immediately
    onSubmit(lineItems.filter((item) => item.id !== id));
  };

  const handleChange = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
    setDirtyItems((prev) => new Set(prev).add(id));
  };

  const autoResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = e.target.scrollHeight + "px";
  };

  const handleBlur = () => {
    if (dirtyItems.size > 0) {
      onSubmit(lineItems);
      setDirtyItems(new Set()); // Clear dirty items after submission
    }
  };

  return (
    <div className="form-container">
      {lineItems.map((item, index) => (
        <DraggableLineItem
          key={item.id}
          id={item.id}
          index={index}
          moveItem={moveItem}
        >
          <div className="line-number">{index + 1}</div>

          <textarea
            value={item.title}
            onChange={(e) => handleChange(item.id, "title", e.target.value)}
            onInput={autoResize}
            onBlur={handleBlur}
            placeholder="Title here"
            className="input title-input"
            disabled={userAccess !== "write"}
            rows={1}
          />

          <textarea
            value={item.type}
            onChange={(e) => handleChange(item.id, "type", e.target.value)}
            onInput={autoResize}
            onBlur={handleBlur}
            placeholder="Type here"
            className="input type-input"
            disabled={userAccess !== "write"}
            rows={1}
          />

          {showAmountFields && (
            <div className="amount-group">
              <div className="amount-unit-group">
                <input
                  type="number"
                  value={item.amount}
                  onChange={(e) =>
                    handleChange(item.id, "amount", e.target.value)
                  }
                  onBlur={handleBlur}
                  placeholder="Amount"
                  className="input amount-input"
                  disabled={userAccess !== "write"}
                />

                <select
                  value={item.unit}
                  onChange={(e) =>
                    handleChange(item.id, "unit", e.target.value)
                  }
                  onBlur={handleBlur}
                  className="input unit-select"
                  disabled={userAccess !== "write"}
                >
                  {units.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="icon-button menu-button">⋮</div>

          <button
            type="button"
            onClick={() => handleRemoveLine(item.id)}
            className="icon-button remove-button"
            disabled={userAccess !== "write"}
          >
            ✕
          </button>
        </DraggableLineItem>
      ))}

      <div className="add-line-container">
        <div className="line-number">{lineItems.length + 1}</div>
        <button
          type="button"
          onClick={handleAddLine}
          className="add-line-button"
          disabled={userAccess !== "write"}
        >
          <img src={plusIcon} alt="Plus Icon" width={16} height={16} />
        </button>
      </div>

      <style jsx>{`
        .form-container {
          width: 100%;
          max-width: 1000px;
          padding: 1rem;
          background-color: rgba(167, 193, 191, 0.15);
          clip-path: polygon(
            99.8% 11.066%,
            99.8% 11.066%,
            99.72% 37.136%,
            99.852% 57.557%,
            99.971% 73.017%,
            99.85% 84.201%,
            99.266% 91.794%,
            97.993% 96.483%,
            95.806% 98.952%,
            92.479% 99.889%,
            87.788% 99.977%,
            81.507% 99.904%,
            81.507% 99.904%,
            73.848% 99.904%,
            65.037% 99.904%,
            55.526% 99.904%,
            45.766% 99.904%,
            36.209% 99.905%,
            27.305% 99.905%,
            19.507% 99.905%,
            13.265% 99.905%,
            9.031% 99.905%,
            7.256% 99.905%,
            7.256% 99.905%,
            7.272% 99.887%,
            7.264% 99.883%,
            7.227% 99.887%,
            7.157% 99.893%,
            7.048% 99.895%,
            6.898% 99.889%,
            6.7% 99.867%,
            6.451% 99.825%,
            6.145% 99.756%,
            5.78% 99.656%,
            5.78% 99.656%,
            3.433% 98.11%,
            1.82% 95.452%,
            0.817% 91.771%,
            0.299% 87.154%,
            0.143% 81.692%,
            0.225% 75.472%,
            0.423% 68.584%,
            0.611% 61.115%,
            0.667% 53.154%,
            0.466% 44.791%,
            0.466% 44.791%,
            0.471% 36.03%,
            0.407% 28.29%,
            0.36% 21.546%,
            0.419% 15.777%,
            0.669% 10.958%,
            1.199% 7.065%,
            2.095% 4.076%,
            3.445% 1.967%,
            5.335% 0.713%,
            7.853% 0.293%,
            7.853% 0.293%,
            32.007% 0.426%,
            51.451% 0.305%,
            66.705% 0.105%,
            78.289% 0%,
            86.722% 0.162%,
            92.522% 0.766%,
            96.211% 1.985%,
            98.307% 3.992%,
            99.33% 6.961%,
            99.8% 11.066%
          );
        }

        .line-item {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.5rem;
          margin-bottom: 0.5rem;
          border-radius: 8px;
          transition: all 0.2s ease;
          background-color: none;
        }

        .line-item:hover {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .line-number {
          width: 2rem;
          color: #193666;
          font-weight: 500;
          text-align: center;
          clip-path: polygon(
            3.034% 36.165%,
            3.034% 36.165%,
            3.042% 38.975%,
            3.065% 41.621%,
            3.096% 44.116%,
            3.129% 46.471%,
            3.16% 48.695%,
            3.182% 50.802%,
            3.19% 52.802%,
            3.178% 54.706%,
            3.141% 56.527%,
            3.074% 58.274%,
            3.074% 58.274%,
            3.053% 58.638%,
            3.039% 58.95%,
            3.031% 59.244%,
            3.027% 59.553%,
            3.028% 59.908%,
            3.033% 60.344%,
            3.041% 60.893%,
            3.051% 61.587%,
            3.062% 62.46%,
            3.074% 63.545%,
            3.074% 63.545%,
            3.076% 64.814%,
            3.064% 66.323%,
            3.044% 68.042%,
            3.023% 69.942%,
            3.008% 71.994%,
            3.007% 74.168%,
            3.026% 76.435%,
            3.072% 78.765%,
            3.153% 81.129%,
            3.275% 83.498%,
            3.275% 83.498%,
            3.329% 84.284%,
            3.394% 85.141%,
            3.473% 86.067%,
            3.567% 87.057%,
            3.679% 88.11%,
            3.81% 89.223%,
            3.962% 90.392%,
            4.138% 91.615%,
            4.338% 92.889%,
            4.566% 94.211%,
            4.566% 94.211%,
            4.745% 94.475%,
            4.944% 94.751%,
            5.161% 95.035%,
            5.396% 95.323%,
            5.65% 95.61%,
            5.922% 95.891%,
            6.213% 96.164%,
            6.52% 96.422%,
            6.846% 96.662%,
            7.188% 96.88%,
            7.188% 96.88%,
            15.141% 96.852%,
            23.418% 96.828%,
            31.955% 96.808%,
            40.688% 96.795%,
            49.55% 96.786%,
            58.478% 96.784%,
            67.406% 96.789%,
            76.27% 96.801%,
            85.005% 96.82%,
            93.545% 96.846%,
            93.545% 96.846%,
            93.986% 96.622%,
            94.411% 96.382%,
            94.819% 96.129%,
            95.21% 95.867%,
            95.583% 95.597%,
            95.936% 95.323%,
            96.269% 95.048%,
            96.58% 94.775%,
            96.869% 94.506%,
            97.135% 94.245%,
            97.135% 94.245%,
            97.247% 93.307%,
            97.36% 92.281%,
            97.475% 91.171%,
            97.589% 89.978%,
            97.7% 88.705%,
            97.806% 87.355%,
            97.906% 85.931%,
            97.996% 84.435%,
            98.076% 82.871%,
            98.144% 81.24%,
            98.144% 81.24%,
            98.172% 80.008%,
            98.186% 78.919%,
            98.186% 77.903%,
            98.177% 76.892%,
            98.159% 75.819%,
            98.135% 74.616%,
            98.108% 73.214%,
            98.078% 71.546%,
            98.049% 69.543%,
            98.023% 67.139%,
            98.023% 67.139%,
            98.012% 65.751%,
            98.004% 64.322%,
            97.998% 62.824%,
            97.994% 61.231%,
            97.993% 59.515%,
            97.994% 57.649%,
            97.998% 55.606%,
            98.004% 53.359%,
            98.012% 50.88%,
            98.023% 48.143%,
            98.023% 48.143%,
            98.034% 46.098%,
            98.043% 44.45%,
            98.049% 43.119%,
            98.055% 42.027%,
            98.058% 41.097%,
            98.061% 40.25%,
            98.062% 39.409%,
            98.063% 38.494%,
            98.063% 37.427%,
            98.063% 36.13%,
            98.063% 36.13%,
            98.063% 35.868%,
            98.062% 35.121%,
            98.06% 33.947%,
            98.055% 32.405%,
            98.048% 30.556%,
            98.037% 28.457%,
            98.022% 26.167%,
            98.001% 23.746%,
            97.975% 21.252%,
            97.942% 18.744%,
            97.942% 18.744%,
            97.931% 18.242%,
            97.923% 17.751%,
            97.916% 17.28%,
            97.911% 16.839%,
            97.907% 16.438%,
            97.905% 16.087%,
            97.903% 15.797%,
            97.902% 15.577%,
            97.902% 15.438%,
            97.902% 15.39%,
            97.902% 15.39%,
            97.89% 14.517%,
            97.878% 13.809%,
            97.867% 13.241%,
            97.856% 12.784%,
            97.847% 12.412%,
            97.838% 12.097%,
            97.831% 11.813%,
            97.826% 11.532%,
            97.822% 11.228%,
            97.821% 10.872%,
            97.821% 10.872%,
            97.805% 9.456%,
            97.8% 8.248%,
            97.803% 7.217%,
            97.808% 6.334%,
            97.811% 5.567%,
            97.807% 4.886%,
            97.79% 4.262%,
            97.757% 3.662%,
            97.702% 3.058%,
            97.62% 2.418%,
            97.62% 2.418%,
            97.532% 2.106%,
            97.375% 1.809%,
            97.16% 1.533%,
            96.899% 1.282%,
            96.606% 1.062%,
            96.292% 0.878%,
            95.97% 0.735%,
            95.653% 0.639%,
            95.351% 0.593%,
            95.079% 0.604%,
            95.079% 0.604%,
            94.702% 0.655%,
            94.346% 0.686%,
            94.01% 0.699%,
            93.691% 0.698%,
            93.39% 0.686%,
            93.104% 0.663%,
            92.832% 0.635%,
            92.575% 0.602%,
            92.329% 0.568%,
            92.094% 0.536%,
            92.094% 0.536%,
            92.029% 0.535%,
            91.884% 0.532%,
            91.658% 0.527%,
            91.35% 0.519%,
            90.959% 0.51%,
            90.485% 0.499%,
            89.926% 0.486%,
            89.282% 0.47%,
            88.553% 0.453%,
            87.737% 0.433%,
            87.737% 0.433%,
            86.384% 0.403%,
            85.089% 0.376%,
            83.857% 0.35%,
            82.696% 0.326%,
            81.611% 0.305%,
            80.611% 0.285%,
            79.7% 0.268%,
            78.886% 0.252%,
            78.174% 0.239%,
            77.573% 0.228%,
            77.573% 0.228%,
            75.594% 0.199%,
            73.705% 0.174%,
            71.91% 0.152%,
            70.218% 0.133%,
            68.634% 0.117%,
            67.165% 0.102%,
            65.817% 0.089%,
            64.597% 0.078%,
            63.512% 0.067%,
            62.568% 0.057%,
            62.568% 0.057%,
            62.18% 0.057%,
            61.805% 0.056%,
            61.394% 0.056%,
            60.897% 0.054%,
            60.264% 0.052%,
            59.446% 0.049%,
            58.394% 0.045%,
            57.057% 0.039%,
            55.386% 0.032%,
            53.331% 0.022%,
            53.331% 0.022%,
            49.947% 0.013%,
            46.763% 0.006%,
            43.818% 0.002%,
            41.146% 0%,
            38.786% 0.001%,
            36.772% 0.005%,
            35.142% 0.013%,
            33.933% 0.024%,
            33.181% 0.038%,
            32.922% 0.057%,
            32.922% 0.057%,
            32.65% 0.073%,
            31.834% 0.083%,
            30.474% 0.087%,
            28.571% 0.086%,
            26.125% 0.082%,
            23.138% 0.076%,
            19.609% 0.07%,
            15.538% 0.063%,
            10.927% 0.059%,
            5.776% 0.057%,
            5.776% 0.057%,
            5.666% 0.054%,
            5.53% 0.053%,
            5.374% 0.061%,
            5.201% 0.09%,
            5.015% 0.147%,
            4.821% 0.242%,
            4.622% 0.384%,
            4.424% 0.582%,
            4.229% 0.847%,
            4.042% 1.186%,
            4.042% 1.186%,
            3.766% 1.89%,
            3.579% 2.702%,
            3.466% 3.645%,
            3.409% 4.741%,
            3.392% 6.012%,
            3.397% 7.48%,
            3.409% 9.167%,
            3.411% 11.096%,
            3.385% 13.288%,
            3.316% 15.766%,
            3.316% 15.766%,
            3.238% 18.085%,
            3.136% 20.224%,
            3.021% 22.227%,
            2.909% 24.139%,
            2.812% 26.004%,
            2.744% 27.866%,
            2.719% 29.768%,
            2.749% 31.756%,
            2.85% 33.874%,
            3.034% 36.165%
          );
          background-color: rgba(255, 255, 255, 0.8);
        }

        .input {
          padding: 0.5rem;
          border: none;
          outline: none;
          border-radius: 4px;
          background-color: white;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .input:focus {
          outline: none;
          border-color: none;
        }

        textarea.input {
          resize: vertical !important;
          overflow: auto !important;
          min-height: auto !important;
          line-height: 1.5 !important;
          vertical-align: top !important;
        }

        textarea.title-input,
        textarea.type-input {
          background-color: rgba(255, 255, 255, 0.8) !important;
          color: #193666 !important;
          width: auto !important;
        }

        .title-input {
          clip-path: polygon(
            1.329% 37.329%,
            1.329% 37.329%,
            1.338% 40.23%,
            1.362% 42.962%,
            1.394% 45.537%,
            1.428% 47.967%,
            1.46% 50.263%,
            1.483% 52.438%,
            1.491% 54.502%,
            1.479% 56.468%,
            1.441% 58.347%,
            1.371% 60.15%,
            1.371% 60.15%,
            1.349% 60.526%,
            1.335% 60.849%,
            1.326% 61.152%,
            1.323% 61.47%,
            1.324% 61.837%,
            1.329% 62.287%,
            1.337% 62.853%,
            1.347% 63.57%,
            1.358% 64.471%,
            1.371% 65.591%,
            1.371% 65.591%,
            1.373% 66.901%,
            1.36% 68.459%,
            1.34% 70.233%,
            1.318% 72.195%,
            1.303% 74.312%,
            1.302% 76.556%,
            1.321% 78.896%,
            1.369% 81.301%,
            1.453% 83.742%,
            1.58% 86.187%,
            1.58% 86.187%,
            1.635% 86.998%,
            1.703% 87.883%,
            1.784% 88.838%,
            1.882% 89.86%,
            1.997% 90.947%,
            2.133% 92.096%,
            2.29% 93.302%,
            2.472% 94.565%,
            2.68% 95.88%,
            2.916% 97.244%,
            2.916% 97.244%,
            3.101% 97.517%,
            3.306% 97.802%,
            3.531% 98.096%,
            3.775% 98.393%,
            4.038% 98.689%,
            4.319% 98.979%,
            4.62% 99.26%,
            4.938% 99.527%,
            5.275% 99.775%,
            5.629% 100%,
            5.629% 100%,
            13.86% 99.97%,
            22.427% 99.945%,
            31.263% 99.926%,
            40.301% 99.911%,
            49.474% 99.903%,
            58.714% 99.901%,
            67.955% 99.906%,
            77.129% 99.918%,
            86.169% 99.937%,
            95.009% 99.965%,
            95.009% 99.965%,
            95.464% 99.733%,
            95.904% 99.485%,
            96.327% 99.224%,
            96.731% 98.953%,
            97.117% 98.675%,
            97.482% 98.393%,
            97.827% 98.109%,
            98.149% 97.827%,
            98.449% 97.549%,
            98.724% 97.28%,
            98.724% 97.28%,
            98.839% 96.311%,
            98.957% 95.253%,
            99.076% 94.106%,
            99.194% 92.875%,
            99.309% 91.561%,
            99.419% 90.168%,
            99.521% 88.698%,
            99.615% 87.154%,
            99.698% 85.539%,
            99.768% 83.855%,
            99.768% 83.855%,
            99.797% 82.585%,
            99.811% 81.46%,
            99.812% 80.411%,
            99.802% 79.368%,
            99.783% 78.26%,
            99.759% 77.018%,
            99.73% 75.571%,
            99.7% 73.85%,
            99.67% 71.783%,
            99.643% 69.3%,
            99.643% 69.3%,
            99.631% 67.868%,
            99.623% 66.393%,
            99.616% 64.847%,
            99.613% 63.203%,
            99.611% 61.431%,
            99.613% 59.505%,
            99.616% 57.396%,
            99.623% 55.077%,
            99.631% 52.518%,
            99.643% 49.693%,
            99.643% 49.693%,
            99.654% 47.583%,
            99.663% 45.881%,
            99.67% 44.507%,
            99.675% 43.38%,
            99.679% 42.421%,
            99.682% 41.547%,
            99.683% 40.678%,
            99.684% 39.733%,
            99.684% 38.632%,
            99.684% 37.294%,
            99.684% 37.294%,
            99.684% 37.023%,
            99.683% 36.251%,
            99.681% 35.04%,
            99.676% 33.449%,
            99.669% 31.54%,
            99.657% 29.373%,
            99.641% 27.01%,
            99.62% 24.51%,
            99.593% 21.936%,
            99.559% 19.347%,
            99.559% 19.347%,
            99.548% 18.83%,
            99.539% 18.323%,
            99.532% 17.836%,
            99.526% 17.381%,
            99.523% 16.967%,
            99.52% 16.605%,
            99.519% 16.306%,
            99.518% 16.079%,
            99.518% 15.935%,
            99.518% 15.885%,
            99.518% 15.885%,
            99.505% 14.984%,
            99.493% 14.254%,
            99.481% 13.667%,
            99.47% 13.196%,
            99.46% 12.812%,
            99.451% 12.487%,
            99.444% 12.194%,
            99.439% 11.904%,
            99.435% 11.589%,
            99.434% 11.222%,
            99.434% 11.222%,
            99.417% 9.761%,
            99.412% 8.514%,
            99.415% 7.45%,
            99.421% 6.538%,
            99.423% 5.746%,
            99.419% 5.044%,
            99.402% 4.399%,
            99.367% 3.78%,
            99.31% 3.156%,
            99.225% 2.496%,
            99.225% 2.496%,
            99.135% 2.174%,
            98.972% 1.867%,
            98.749% 1.582%,
            98.48% 1.323%,
            98.176% 1.096%,
            97.851% 0.906%,
            97.518% 0.759%,
            97.189% 0.659%,
            96.877% 0.612%,
            96.595% 0.624%,
            96.595% 0.624%,
            96.205% 0.676%,
            95.837% 0.708%,
            95.489% 0.722%,
            95.159% 0.721%,
            94.847% 0.708%,
            94.551% 0.685%,
            94.27% 0.655%,
            94.004% 0.621%,
            93.749% 0.587%,
            93.506% 0.553%,
            93.506% 0.553%,
            93.439% 0.552%,
            93.289% 0.549%,
            93.055% 0.544%,
            92.736% 0.536%,
            92.332% 0.527%,
            91.841% 0.515%,
            91.262% 0.501%,
            90.596% 0.485%,
            89.841% 0.467%,
            88.997% 0.447%,
            88.997% 0.447%,
            87.597% 0.416%,
            86.256% 0.388%,
            84.981% 0.361%,
            83.779% 0.337%,
            82.657% 0.315%,
            81.621% 0.294%,
            80.678% 0.276%,
            79.836% 0.261%,
            79.1% 0.247%,
            78.477% 0.235%,
            78.477% 0.235%,
            76.429% 0.205%,
            74.473% 0.179%,
            72.616% 0.157%,
            70.864% 0.137%,
            69.225% 0.12%,
            67.704% 0.105%,
            66.31% 0.092%,
            65.047% 0.08%,
            63.924% 0.069%,
            62.947% 0.059%,
            62.947% 0.059%,
            62.545% 0.058%,
            62.157% 0.058%,
            61.732% 0.058%,
            61.217% 0.056%,
            60.562% 0.054%,
            59.716% 0.051%,
            58.626% 0.046%,
            57.243% 0.04%,
            55.513% 0.033%,
            53.387% 0.023%,
            53.387% 0.023%,
            49.884% 0.014%,
            46.589% 0.007%,
            43.541% 0.002%,
            40.776% 0%,
            38.332% 0.001%,
            36.248% 0.005%,
            34.562% 0.013%,
            33.31% 0.024%,
            32.531% 0.039%,
            32.263% 0.059%,
            32.263% 0.059%,
            31.982% 0.076%,
            31.137% 0.086%,
            29.73% 0.09%,
            27.76% 0.089%,
            25.229% 0.085%,
            22.137% 0.079%,
            18.484% 0.072%,
            14.272% 0.065%,
            9.499% 0.06%,
            4.168% 0.059%,
            4.168% 0.059%,
            4.053% 0.056%,
            3.913% 0.054%,
            3.751% 0.063%,
            3.572% 0.092%,
            3.38% 0.151%,
            3.179% 0.249%,
            2.974% 0.396%,
            2.768% 0.601%,
            2.566% 0.874%,
            2.373% 1.224%,
            2.373% 1.224%,
            2.087% 1.951%,
            1.894% 2.79%,
            1.777% 3.763%,
            1.718% 4.894%,
            1.7% 6.206%,
            1.706% 7.721%,
            1.718% 9.462%,
            1.72% 11.453%,
            1.693% 13.716%,
            1.622% 16.274%,
            1.622% 16.274%,
            1.541% 18.667%,
            1.435% 20.875%,
            1.317% 22.943%,
            1.2% 24.916%,
            1.1% 26.841%,
            1.03% 28.763%,
            1.003% 30.727%,
            1.035% 32.779%,
            1.139% 34.964%,
            1.329% 37.329%
          );
          background-color: rgba(255, 255, 255, 0.8);
          color: #193666;
        }

        .title-input,
        .type-input {
          flex: 1;
          font-size: 12px;
        }

        .type-input {
          clip-path: polygon(
            1.329% 37.329%,
            1.329% 37.329%,
            1.338% 40.23%,
            1.362% 42.962%,
            1.394% 45.537%,
            1.428% 47.967%,
            1.46% 50.263%,
            1.483% 52.438%,
            1.491% 54.502%,
            1.479% 56.468%,
            1.441% 58.347%,
            1.371% 60.15%,
            1.371% 60.15%,
            1.349% 60.526%,
            1.335% 60.849%,
            1.326% 61.152%,
            1.323% 61.47%,
            1.324% 61.837%,
            1.329% 62.287%,
            1.337% 62.853%,
            1.347% 63.57%,
            1.358% 64.471%,
            1.371% 65.591%,
            1.371% 65.591%,
            1.373% 66.901%,
            1.36% 68.459%,
            1.34% 70.233%,
            1.318% 72.195%,
            1.303% 74.312%,
            1.302% 76.556%,
            1.321% 78.896%,
            1.369% 81.301%,
            1.453% 83.742%,
            1.58% 86.187%,
            1.58% 86.187%,
            1.635% 86.998%,
            1.703% 87.883%,
            1.784% 88.838%,
            1.882% 89.86%,
            1.997% 90.947%,
            2.133% 92.096%,
            2.29% 93.302%,
            2.472% 94.565%,
            2.68% 95.88%,
            2.916% 97.244%,
            2.916% 97.244%,
            3.101% 97.517%,
            3.306% 97.802%,
            3.531% 98.096%,
            3.775% 98.393%,
            4.038% 98.689%,
            4.319% 98.979%,
            4.62% 99.26%,
            4.938% 99.527%,
            5.275% 99.775%,
            5.629% 100%,
            5.629% 100%,
            13.86% 99.97%,
            22.427% 99.945%,
            31.263% 99.926%,
            40.301% 99.911%,
            49.474% 99.903%,
            58.714% 99.901%,
            67.955% 99.906%,
            77.129% 99.918%,
            86.169% 99.937%,
            95.009% 99.965%,
            95.009% 99.965%,
            95.464% 99.733%,
            95.904% 99.485%,
            96.327% 99.224%,
            96.731% 98.953%,
            97.117% 98.675%,
            97.482% 98.393%,
            97.827% 98.109%,
            98.149% 97.827%,
            98.449% 97.549%,
            98.724% 97.28%,
            98.724% 97.28%,
            98.839% 96.311%,
            98.957% 95.253%,
            99.076% 94.106%,
            99.194% 92.875%,
            99.309% 91.561%,
            99.419% 90.168%,
            99.521% 88.698%,
            99.615% 87.154%,
            99.698% 85.539%,
            99.768% 83.855%,
            99.768% 83.855%,
            99.797% 82.585%,
            99.811% 81.46%,
            99.812% 80.411%,
            99.802% 79.368%,
            99.783% 78.26%,
            99.759% 77.018%,
            99.73% 75.571%,
            99.7% 73.85%,
            99.67% 71.783%,
            99.643% 69.3%,
            99.643% 69.3%,
            99.631% 67.868%,
            99.623% 66.393%,
            99.616% 64.847%,
            99.613% 63.203%,
            99.611% 61.431%,
            99.613% 59.505%,
            99.616% 57.396%,
            99.623% 55.077%,
            99.631% 52.518%,
            99.643% 49.693%,
            99.643% 49.693%,
            99.654% 47.583%,
            99.663% 45.881%,
            99.67% 44.507%,
            99.675% 43.38%,
            99.679% 42.421%,
            99.682% 41.547%,
            99.683% 40.678%,
            99.684% 39.733%,
            99.684% 38.632%,
            99.684% 37.294%,
            99.684% 37.294%,
            99.684% 37.023%,
            99.683% 36.251%,
            99.681% 35.04%,
            99.676% 33.449%,
            99.669% 31.54%,
            99.657% 29.373%,
            99.641% 27.01%,
            99.62% 24.51%,
            99.593% 21.936%,
            99.559% 19.347%,
            99.559% 19.347%,
            99.548% 18.83%,
            99.539% 18.323%,
            99.532% 17.836%,
            99.526% 17.381%,
            99.523% 16.967%,
            99.52% 16.605%,
            99.519% 16.306%,
            99.518% 16.079%,
            99.518% 15.935%,
            99.518% 15.885%,
            99.518% 15.885%,
            99.505% 14.984%,
            99.493% 14.254%,
            99.481% 13.667%,
            99.47% 13.196%,
            99.46% 12.812%,
            99.451% 12.487%,
            99.444% 12.194%,
            99.439% 11.904%,
            99.435% 11.589%,
            99.434% 11.222%,
            99.434% 11.222%,
            99.417% 9.761%,
            99.412% 8.514%,
            99.415% 7.45%,
            99.421% 6.538%,
            99.423% 5.746%,
            99.419% 5.044%,
            99.402% 4.399%,
            99.367% 3.78%,
            99.31% 3.156%,
            99.225% 2.496%,
            99.225% 2.496%,
            99.135% 2.174%,
            98.972% 1.867%,
            98.749% 1.582%,
            98.48% 1.323%,
            98.176% 1.096%,
            97.851% 0.906%,
            97.518% 0.759%,
            97.189% 0.659%,
            96.877% 0.612%,
            96.595% 0.624%,
            96.595% 0.624%,
            96.205% 0.676%,
            95.837% 0.708%,
            95.489% 0.722%,
            95.159% 0.721%,
            94.847% 0.708%,
            94.551% 0.685%,
            94.27% 0.655%,
            94.004% 0.621%,
            93.749% 0.587%,
            93.506% 0.553%,
            93.506% 0.553%,
            93.439% 0.552%,
            93.289% 0.549%,
            93.055% 0.544%,
            92.736% 0.536%,
            92.332% 0.527%,
            91.841% 0.515%,
            91.262% 0.501%,
            90.596% 0.485%,
            89.841% 0.467%,
            88.997% 0.447%,
            88.997% 0.447%,
            87.597% 0.416%,
            86.256% 0.388%,
            84.981% 0.361%,
            83.779% 0.337%,
            82.657% 0.315%,
            81.621% 0.294%,
            80.678% 0.276%,
            79.836% 0.261%,
            79.1% 0.247%,
            78.477% 0.235%,
            78.477% 0.235%,
            76.429% 0.205%,
            74.473% 0.179%,
            72.616% 0.157%,
            70.864% 0.137%,
            69.225% 0.12%,
            67.704% 0.105%,
            66.31% 0.092%,
            65.047% 0.08%,
            63.924% 0.069%,
            62.947% 0.059%,
            62.947% 0.059%,
            62.545% 0.058%,
            62.157% 0.058%,
            61.732% 0.058%,
            61.217% 0.056%,
            60.562% 0.054%,
            59.716% 0.051%,
            58.626% 0.046%,
            57.243% 0.04%,
            55.513% 0.033%,
            53.387% 0.023%,
            53.387% 0.023%,
            49.884% 0.014%,
            46.589% 0.007%,
            43.541% 0.002%,
            40.776% 0%,
            38.332% 0.001%,
            36.248% 0.005%,
            34.562% 0.013%,
            33.31% 0.024%,
            32.531% 0.039%,
            32.263% 0.059%,
            32.263% 0.059%,
            31.982% 0.076%,
            31.137% 0.086%,
            29.73% 0.09%,
            27.76% 0.089%,
            25.229% 0.085%,
            22.137% 0.079%,
            18.484% 0.072%,
            14.272% 0.065%,
            9.499% 0.06%,
            4.168% 0.059%,
            4.168% 0.059%,
            4.053% 0.056%,
            3.913% 0.054%,
            3.751% 0.063%,
            3.572% 0.092%,
            3.38% 0.151%,
            3.179% 0.249%,
            2.974% 0.396%,
            2.768% 0.601%,
            2.566% 0.874%,
            2.373% 1.224%,
            2.373% 1.224%,
            2.087% 1.951%,
            1.894% 2.79%,
            1.777% 3.763%,
            1.718% 4.894%,
            1.7% 6.206%,
            1.706% 7.721%,
            1.718% 9.462%,
            1.72% 11.453%,
            1.693% 13.716%,
            1.622% 16.274%,
            1.622% 16.274%,
            1.541% 18.667%,
            1.435% 20.875%,
            1.317% 22.943%,
            1.2% 24.916%,
            1.1% 26.841%,
            1.03% 28.763%,
            1.003% 30.727%,
            1.035% 32.779%,
            1.139% 34.964%,
            1.329% 37.329%
          );
          background-color: rgba(255, 255, 255, 0.8);
          color: #193666;
        }

        .amount-group {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .amount-unit-group {
          display: flex;
          align-items: center;
          gap: 0;
        }

        .title-input::placeholder,
        .type-input::placeholder,
        .amount-input::placeholder,
        .unit-select::placeholder {
          color: #193666;
        }

        .amount-input {
          width: 8rem;
          font-size: 12px;
        }

        .unit-select {
          width: 6rem;
          font-size: 12px;
        }

        .icon-button {
          padding: 0.5rem;
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .remove-button:hover {
          color: #ef4444;
        }

        .menu-button {
          cursor: grab;
        }

        .menu-button:hover {
          color: #374151;
        }

        .add-line-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-top: 1rem;
          padding: 0.5rem;
        }

        .add-line-button {
          display: flex;
          flex: 1;
          font-size: 12px;
          padding: 0.5rem;
          align-items: center;
          justify-content: center;
          max-width: 22%;
          background: none;
          border: none;
          border-radius: 4px;
          color: #1b3764;
          cursor: pointer;
          transition: all 0.2s ease;
          clip-path: polygon(
            1.329% 37.329%,
            1.329% 37.329%,
            1.338% 40.23%,
            1.362% 42.962%,
            1.394% 45.537%,
            1.428% 47.967%,
            1.46% 50.263%,
            1.483% 52.438%,
            1.491% 54.502%,
            1.479% 56.468%,
            1.441% 58.347%,
            1.371% 60.15%,
            1.371% 60.15%,
            1.349% 60.526%,
            1.335% 60.849%,
            1.326% 61.152%,
            1.323% 61.47%,
            1.324% 61.837%,
            1.329% 62.287%,
            1.337% 62.853%,
            1.347% 63.57%,
            1.358% 64.471%,
            1.371% 65.591%,
            1.371% 65.591%,
            1.373% 66.901%,
            1.36% 68.459%,
            1.34% 70.233%,
            1.318% 72.195%,
            1.303% 74.312%,
            1.302% 76.556%,
            1.321% 78.896%,
            1.369% 81.301%,
            1.453% 83.742%,
            1.58% 86.187%,
            1.58% 86.187%,
            1.635% 86.998%,
            1.703% 87.883%,
            1.784% 88.838%,
            1.882% 89.86%,
            1.997% 90.947%,
            2.133% 92.096%,
            2.29% 93.302%,
            2.472% 94.565%,
            2.68% 95.88%,
            2.916% 97.244%,
            2.916% 97.244%,
            3.101% 97.517%,
            3.306% 97.802%,
            3.531% 98.096%,
            3.775% 98.393%,
            4.038% 98.689%,
            4.319% 98.979%,
            4.62% 99.26%,
            4.938% 99.527%,
            5.275% 99.775%,
            5.629% 100%,
            5.629% 100%,
            13.86% 99.97%,
            22.427% 99.945%,
            31.263% 99.926%,
            40.301% 99.911%,
            49.474% 99.903%,
            58.714% 99.901%,
            67.955% 99.906%,
            77.129% 99.918%,
            86.169% 99.937%,
            95.009% 99.965%,
            95.009% 99.965%,
            95.464% 99.733%,
            95.904% 99.485%,
            96.327% 99.224%,
            96.731% 98.953%,
            97.117% 98.675%,
            97.482% 98.393%,
            97.827% 98.109%,
            98.149% 97.827%,
            98.449% 97.549%,
            98.724% 97.28%,
            98.724% 97.28%,
            98.839% 96.311%,
            98.957% 95.253%,
            99.076% 94.106%,
            99.194% 92.875%,
            99.309% 91.561%,
            99.419% 90.168%,
            99.521% 88.698%,
            99.615% 87.154%,
            99.698% 85.539%,
            99.768% 83.855%,
            99.768% 83.855%,
            99.797% 82.585%,
            99.811% 81.46%,
            99.812% 80.411%,
            99.802% 79.368%,
            99.783% 78.26%,
            99.759% 77.018%,
            99.73% 75.571%,
            99.7% 73.85%,
            99.67% 71.783%,
            99.643% 69.3%,
            99.643% 69.3%,
            99.631% 67.868%,
            99.623% 66.393%,
            99.616% 64.847%,
            99.613% 63.203%,
            99.611% 61.431%,
            99.613% 59.505%,
            99.616% 57.396%,
            99.623% 55.077%,
            99.631% 52.518%,
            99.643% 49.693%,
            99.643% 49.693%,
            99.654% 47.583%,
            99.663% 45.881%,
            99.67% 44.507%,
            99.675% 43.38%,
            99.679% 42.421%,
            99.682% 41.547%,
            99.683% 40.678%,
            99.684% 39.733%,
            99.684% 38.632%,
            99.684% 37.294%,
            99.684% 37.294%,
            99.684% 37.023%,
            99.683% 36.251%,
            99.681% 35.04%,
            99.676% 33.449%,
            99.669% 31.54%,
            99.657% 29.373%,
            99.641% 27.01%,
            99.62% 24.51%,
            99.593% 21.936%,
            99.559% 19.347%,
            99.559% 19.347%,
            99.548% 18.83%,
            99.539% 18.323%,
            99.532% 17.836%,
            99.526% 17.381%,
            99.523% 16.967%,
            99.52% 16.605%,
            99.519% 16.306%,
            99.518% 16.079%,
            99.518% 15.935%,
            99.518% 15.885%,
            99.518% 15.885%,
            99.505% 14.984%,
            99.493% 14.254%,
            99.481% 13.667%,
            99.47% 13.196%,
            99.46% 12.812%,
            99.451% 12.487%,
            99.444% 12.194%,
            99.439% 11.904%,
            99.435% 11.589%,
            99.434% 11.222%,
            99.434% 11.222%,
            99.417% 9.761%,
            99.412% 8.514%,
            99.415% 7.45%,
            99.421% 6.538%,
            99.423% 5.746%,
            99.419% 5.044%,
            99.402% 4.399%,
            99.367% 3.78%,
            99.31% 3.156%,
            99.225% 2.496%,
            99.225% 2.496%,
            99.135% 2.174%,
            98.972% 1.867%,
            98.749% 1.582%,
            98.48% 1.323%,
            98.176% 1.096%,
            97.851% 0.906%,
            97.518% 0.759%,
            97.189% 0.659%,
            96.877% 0.612%,
            96.595% 0.624%,
            96.595% 0.624%,
            96.205% 0.676%,
            95.837% 0.708%,
            95.489% 0.722%,
            95.159% 0.721%,
            94.847% 0.708%,
            94.551% 0.685%,
            94.27% 0.655%,
            94.004% 0.621%,
            93.749% 0.587%,
            93.506% 0.553%,
            93.506% 0.553%,
            93.439% 0.552%,
            93.289% 0.549%,
            93.055% 0.544%,
            92.736% 0.536%,
            92.332% 0.527%,
            91.841% 0.515%,
            91.262% 0.501%,
            90.596% 0.485%,
            89.841% 0.467%,
            88.997% 0.447%,
            88.997% 0.447%,
            87.597% 0.416%,
            86.256% 0.388%,
            84.981% 0.361%,
            83.779% 0.337%,
            82.657% 0.315%,
            81.621% 0.294%,
            80.678% 0.276%,
            79.836% 0.261%,
            79.1% 0.247%,
            78.477% 0.235%,
            78.477% 0.235%,
            76.429% 0.205%,
            74.473% 0.179%,
            72.616% 0.157%,
            70.864% 0.137%,
            69.225% 0.12%,
            67.704% 0.105%,
            66.31% 0.092%,
            65.047% 0.08%,
            63.924% 0.069%,
            62.947% 0.059%,
            62.947% 0.059%,
            62.545% 0.058%,
            62.157% 0.058%,
            61.732% 0.058%,
            61.217% 0.056%,
            60.562% 0.054%,
            59.716% 0.051%,
            58.626% 0.046%,
            57.243% 0.04%,
            55.513% 0.033%,
            53.387% 0.023%,
            53.387% 0.023%,
            49.884% 0.014%,
            46.589% 0.007%,
            43.541% 0.002%,
            40.776% 0%,
            38.332% 0.001%,
            36.248% 0.005%,
            34.562% 0.013%,
            33.31% 0.024%,
            32.531% 0.039%,
            32.263% 0.059%,
            32.263% 0.059%,
            31.982% 0.076%,
            31.137% 0.086%,
            29.73% 0.09%,
            27.76% 0.089%,
            25.229% 0.085%,
            22.137% 0.079%,
            18.484% 0.072%,
            14.272% 0.065%,
            9.499% 0.06%,
            4.168% 0.059%,
            4.168% 0.059%,
            4.053% 0.056%,
            3.913% 0.054%,
            3.751% 0.063%,
            3.572% 0.092%,
            3.38% 0.151%,
            3.179% 0.249%,
            2.974% 0.396%,
            2.768% 0.601%,
            2.566% 0.874%,
            2.373% 1.224%,
            2.373% 1.224%,
            2.087% 1.951%,
            1.894% 2.79%,
            1.777% 3.763%,
            1.718% 4.894%,
            1.7% 6.206%,
            1.706% 7.721%,
            1.718% 9.462%,
            1.72% 11.453%,
            1.693% 13.716%,
            1.622% 16.274%,
            1.622% 16.274%,
            1.541% 18.667%,
            1.435% 20.875%,
            1.317% 22.943%,
            1.2% 24.916%,
            1.1% 26.841%,
            1.03% 28.763%,
            1.003% 30.727%,
            1.035% 32.779%,
            1.139% 34.964%,
            1.329% 37.329%
          );
          background-color: rgba(255, 255, 255, 0.8);
        }

        .add-line-button:hover {
          color: #1b3764;
        }
      `}</style>
    </div>
  );
};

export default LineItemsForm;
